import "./EvolucionMensual.css";
import { useContext, useEffect, useState } from "react";
import { DarkModeContext } from "../../context/DarkModeContext";
import { formatearAPeso, formatearMonto } from "../../helpers/formatearAPeso";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Form } from "react-bootstrap";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Responsive chico: mismo breakpoint que ya usa Analisis.css para pasar
// la pagina al layout mobile. Se consulta en el momento de dibujar, asi
// el grafico acompana el resize sin estado extra.
const MEDIA_MOBILE_CHICO = "(max-width: 900px)";
const esMobileChico = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(MEDIA_MOBILE_CHICO).matches;

// Eje Y abreviado en mobile: $0, $500k, $1,5M, $20M. Solo la etiqueta
// visible; el valor del dato no cambia.
const formatoAbreviado = new Intl.NumberFormat("es-AR", {
  maximumFractionDigits: 1,
});
const formatearMontoAbreviado = (valor) => {
  const numero = Number(valor) || 0;
  const signo = numero < 0 ? "-" : "";
  const absoluto = Math.abs(numero);
  if (absoluto >= 1000000) {
    return `${signo}$${formatoAbreviado.format(absoluto / 1000000)}M`;
  }
  if (absoluto >= 1000) {
    return `${signo}$${formatoAbreviado.format(absoluto / 1000)}k`;
  }
  return `${signo}$${formatoAbreviado.format(absoluto)}`;
};

const EvolucionMensual3Barras = ({ datosBack }) => {
  const { darkMode } = useContext(DarkModeContext);
  const [modoGrafico, setModoGrafico] = useState("monto");
  const [barraActivaInflacion, setBarraActivaInflacion] = useState(true);
  const [esMobile, setEsMobile] = useState(esMobileChico);
  const { resumenUltimos7Meses } = datosBack;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }
    const media = window.matchMedia(MEDIA_MOBILE_CHICO);
    const actualizar = () => setEsMobile(media.matches);
    actualizar();
    media.addEventListener("change", actualizar);
    return () => media.removeEventListener("change", actualizar);
  }, []);
  const datosResumenUltimosMeses = resumenUltimos7Meses || [];
  const totalBruto7Meses = datosResumenUltimosMeses
    .map((dato) => dato.totalBruto)
    .reverse();
  const totalOperaciones = datosResumenUltimosMeses
    .map((dato) => dato.cantidadDatos)
    .reverse();
  const ultimos7Meses = datosResumenUltimosMeses.map((dato) => dato.mes).reverse();
  const totalBrutoMenosInflacion = datosResumenUltimosMeses
    .map((dato) => dato.totalBrutoMenosInflacion)
    .reverse();

  let meses = ultimos7Meses;
  const mostrandoOperaciones = modoGrafico === "operaciones";
  const mayorOperacion = Math.max(...totalOperaciones.map((valor) => Number(valor) || 0), 0);
  const ejeOperacionesMax = Math.max(5, Math.ceil(mayorOperacion * 1.25));
  const pasoOperaciones = Math.max(1, Math.ceil(ejeOperacionesMax / 4));

  const misoptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        bodyFont: {
          size: 12,
        },
        padding: 10,
        boxWidth: 8,
        boxHeight: 8,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            if (context.parsed.y !== null) {
              if (
                label.includes("Por monto") ||
                label.includes("Ajuste por inflación")
              ) {
                label += formatearAPeso(context.parsed.y);
              } else {
                label += context.parsed.y;
              }
            }
            return label;
          },
        },
      },
    },
    scales: {
      y: {
        display: true,
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          color: darkMode ? "rgba(255, 255, 255, 0.08)" : "#e6e9ee",
          drawTicks: false,
        },
        ticks: {
          color: darkMode ? "#c7ccd3" : "#a0a6af",
          font: {
            size: 11,
          },
          padding: 10,
          stepSize: mostrandoOperaciones ? pasoOperaciones : undefined,
          callback: function (value) {
            if (mostrandoOperaciones) {
              return Number(value).toLocaleString("es-AR");
            }
            if (esMobileChico()) {
              return formatearMontoAbreviado(value);
            }
            return formatearMonto(value).replace(",00", "");
          },
        },
        suggestedMax: mostrandoOperaciones ? ejeOperacionesMax : undefined,
      },
      x: {
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: darkMode ? "#c7ccd3" : "#a0a6af",
          font: {
            size: 11,
          },
          // Solo la etiqueta visible del eje X. Las series, los datos y
          // el tooltip (que sigue mostrando el mes completo) no cambian:
          // en mobile chico se muestra la inicial del mes que ya viene
          // del back en mayusculas (ENERO -> E, FEBRERO -> F, ...).
          callback: function (value) {
            const etiqueta = this.getLabelForValue(value);
            if (!esMobileChico()) {
              return etiqueta;
            }
            return String(etiqueta).trim().charAt(0);
          },
        },
      },
    },
  };

  const midata = {
    labels: meses,
    datasets: mostrandoOperaciones
      ? [
          {
            label: "Por cantidad de operaciones ",
            data: totalOperaciones,
            backgroundColor: "#292B2F",
          },
        ]
      : [
          {
            label: "Por monto $ ",
            data: totalBruto7Meses,
            backgroundColor: "#B4C400",
          },
          {
            label: "Ajuste por inflación $ ",
            data: barraActivaInflacion ? totalBrutoMenosInflacion : [],
            backgroundColor: "#B3B5BF",
          },
        ],
  };

  midata.datasets.forEach(function (dataset) {
    dataset.barPercentage = 1;
    dataset.barThickness = 20;
    dataset.borderRadius = 4;
    if (esMobile) {
      // Mobile: cada barra (o par de barras) centrada sobre su mes.
      // skipNull evita reservar lugar para la serie de inflacion cuando
      // esta apagada (su array queda vacio) y el ancho se adapta al
      // espacio de cada mes (hasta los mismos 20px) para que los pares
      // no se pisen entre meses. Datos, colores y orden no cambian.
      dataset.skipNull = true;
      delete dataset.barThickness;
      dataset.maxBarThickness = 20;
      dataset.barPercentage = 0.9;
      dataset.categoryPercentage = 0.8;
    }
  });

  return (
    <div translate="no" className="aliado-analisis-evolution">
      <section className="container">
        <article className={darkMode ? "bg-grafica-dark" : "bg-grafica"}>
          <div className="aliado-analisis-chart-header">
            <div>
              <h2 className="fs-18">Evolución mensual</h2>
              <span>Comparativa en bruto</span>
            </div>
            <Form className="aliado-analisis-switches">
              <Form.Check
                type="radio"
                id="custom-radio-monto"
                name="analisis-evolucion-metrica"
                label="Monto"
                checked={modoGrafico === "monto"}
                onChange={() => setModoGrafico("monto")}
                className="mx-2 lato-bold fs-12"
              />
              <Form.Check
                type="radio"
                id="custom-radio-operaciones"
                name="analisis-evolucion-metrica"
                label="Operaciones"
                checked={modoGrafico === "operaciones"}
                onChange={() => setModoGrafico("operaciones")}
                className="mx-2 lato-bold fs-12"
              />
              <Form.Check
                type="switch"
                id="custom-switch-inflacion"
                label="Inflación"
                checked={barraActivaInflacion}
                onChange={() => setBarraActivaInflacion(!barraActivaInflacion)}
                disabled={mostrandoOperaciones}
                className="mx-2 lato-bold fs-12 aliado-analisis-inflacion-control"
              />
            </Form>
          </div>
          <div className="aliado-analisis-main-chart">
            <Bar
              className="padding-top-grafica-analisis d-block"
              data={midata}
              options={misoptions}
            />
          </div>
        </article>
      </section>
    </div>
  );
};

export default EvolucionMensual3Barras;
